import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  DialogActions,
  Typography,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { manageFiles } from "../modules/api/docListsandDetails";
import {
  DeleteOutline,
  InsertDriveFile,
  InsertPhoto,
  Link,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import usePhotosDialogStyles from "./css/usePhotosDialogStyles";
import AttachmentPreview from "./AttachmentPreview";
import { useUploadError } from "../hooks";
import Alert from "./Alert";
import { LoaderContext } from "./LoaderProvider";
import { getErrorMessage } from "./form/formUtils";

interface Attachment {
  fileCode: string;
  description?: string;
  fileType?: string;
  file?: string;
  recordType: string | number; // nes backend gali grąžinti abu
  operationCode: string;
  rowNumber?: string | number; // nes backend gali grąžinti abu
}

interface AttachmentsViewerProps {
  open: boolean;
  onClose: () => void;
  attachments: Attachment[];
  clientDbId?: number;
  module?: string;
  operationCode?: string;
  locale?: string;
  onRefresh: () => void;
  rowNumber?: number;
  onRefreshHeader?: () => void;
  onRefreshRow?: () => void;
  fileDelete?: number | string;
}

const AttachmentsViewer: React.FC<AttachmentsViewerProps> = ({
  open,
  onClose,
  attachments,
  clientDbId,
  module,
  operationCode,
  locale,
  onRefresh,
  rowNumber,
  onRefreshHeader,
  onRefreshRow,
  fileDelete,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<string | undefined>(undefined);
  const [previewDescription, setPreviewDescription] = useState<
    string | undefined
  >(undefined);
  const [previewFileType, setPreviewFileType] = useState<string | undefined>(
    undefined
  );
  const [previewRecordType, setPreviewRecordType] = useState<
    string | undefined
  >(undefined);
  const [previewLink, setPreviewLink] = useState<string | undefined>(undefined);

  const { error, showError, clearError } = useUploadError();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { classes } = usePhotosDialogStyles();
  const [isLoading, setLoading] = useContext(LoaderContext);

  const toggleSelection = (fileCode: string) => {
    setSelected((prev) =>
      prev.includes(fileCode)
        ? prev.filter((code) => code !== fileCode)
        : [...prev, fileCode]
    );
  };

  const handleDelete = async (fileCode: string) => {
    try {
      setLoading(true);

      const params: any = {
        clientDbId,
        module,
        operationCode,
        oper: "D",
        locale,
        fileCode,
      };

      if (rowNumber != null) {
        params.rowNumber = String(rowNumber); // visada siunčiamas kaip string
      }

      await manageFiles(params);

      onRefresh();
      if (onRefreshHeader) onRefreshHeader();
      if (onRefreshRow) onRefreshRow();
    } catch (err: any) {
      const { message } = getErrorMessage(err);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      setLoading(true);
      for (const fileCode of selected) {
        await handleDelete(fileCode);
      }
      setSelected([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (att: Attachment) => {
    try {
      setLoading(true);

      const params: any = {
        clientDbId,
        module,
        operationCode: att.operationCode,
        oper: "S",
        fileCode: att.fileCode,
        locale,
      };

      if (att.rowNumber != null && att.rowNumber !== "") {
        params.rowNumber = String(att.rowNumber); // visada kaip string
      }

      const res = await manageFiles(params);

      if ("data" in res && Array.isArray(res.data)) {
        const fileData = res.data[0]?.file;
        const fileType = res.data[0]?.fileType;
        const recordType = String(res.data[0]?.recordType ?? ""); // visada kaip string
        const link = res.data[0]?.link;
        const description = res.data[0]?.description;

        // PDF – atidarome naujame lange
        if (fileType?.toLowerCase().includes("pdf") && fileData) {
          const byteCharacters = atob(fileData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });
          const blobUrl = URL.createObjectURL(blob);

          window.open(blobUrl, "_blank");
          return;
        }

        // Nuoroda, recordType jau string
        if (recordType === "4" && link) {
          window.open(link, "_blank");
          return;
        }

        // Nuotrauka – rodom dialoge
        setPreviewFile(fileData);
        setPreviewDescription(description);
        setPreviewFileType(fileType);
        setPreviewRecordType(recordType);
        setPreviewLink(link);
        setPreviewOpen(true);
      }
    } catch (err: any) {
      const { message } = getErrorMessage(err);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        className={classes.dialogRoot}
      >
        <div className={classes.header}>
          <Typography variant="subtitle1">
            {t("attachedFiles", "Prisegti failai")}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className={classes.previewContent}>
          {attachments.map((att) => {
            const recordType = String(att.recordType ?? ""); // visada kaip string

            return (
              <div
                key={att.fileCode}
                className={classes.attachmentRow}
                style={{ cursor: "pointer" }}
              >
                <div className={classes.attachmentItem}>
                  <Checkbox
                    checked={selected.includes(att.fileCode)}
                    disabled={isLoading}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelection(att.fileCode)}
                  />

                  <div
                    onClick={() => !isLoading && handlePreview(att)}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {recordType === "1" &&
                    (att.fileType?.toLowerCase().includes("jpg") ||
                      att.fileType?.toLowerCase().includes("jpeg") ||
                      att.fileType?.toLowerCase().includes("png")) ? (
                      <InsertPhoto
                        fontSize="large"
                        style={{ marginRight: 6 }}
                      />
                    ) : recordType === "1" ? (
                      <InsertDriveFile
                        fontSize="large"
                        style={{ marginRight: 6 }}
                      />
                    ) : recordType === "4" ? (
                      <Link fontSize="large" style={{ marginRight: 6 }} />
                    ) : null}

                    <div>
                      <Typography variant="body2">{att.fileCode}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.2,
                          fontSize: "0.7rem",
                          display: "block",
                        }}
                      >
                        {isMobile
                          ? (att.description?.length ?? 0) > 32
                            ? att.description?.slice(0, 32) + "..."
                            : att.description || "Be komentaro"
                          : att.description || "Be komentaro"}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: "space-between", padding: "8px 16px" }}
        >
          {Number(fileDelete) > 0 && (
            <Button
              color="error"
              disabled={isLoading}
              onClick={handleDeleteSelected}
              sx={{ textTransform: "none" }}
            >
              <DeleteOutline /> {t("deleteSelected", "Ištrinti pažymėtus")}(
              {selected.length})
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AttachmentPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        file={previewFile}
        description={previewDescription}
        fileType={previewFileType}
        recordType={previewRecordType}
        link={previewLink}
      />

      <Alert
        isOpen={!!error}
        onClose={clearError}
        message={error || ""}
        iconColor="error"
      />
    </>
  );
};

export default AttachmentsViewer;
